// Lemon Squeezy overlay checkout integration.
//
// LS provides a JS SDK at https://app.lemonsqueezy.com/js/lemon.js. Loading
// it attaches `window.createLemonSqueezy` (and `window.LemonSqueezy`).
// Calling `LemonSqueezy.Url.Open(url)` slides up an overlay checkout iframe
// without leaving the page — buyer completes purchase, LS fires our webhook
// at /api/lemonsqueezy/webhook, and we redirect them to /thank-you.
//
// We pass our attribution context as `checkout[custom][...]` query params on
// the buy URL, which surface in the webhook's `data.attributes.first_order_item.custom`
// (or `meta.custom_data`, depending on LS version) so the purchase can be
// joined back to the visitor session for ROAS modeling.
//
// LS docs:
//   https://docs.lemonsqueezy.com/help/checkout/displaying-an-overlay-checkout
//   https://docs.lemonsqueezy.com/help/checkout/customizing-checkout

import {
  getVisitorId,
  getSessionId,
  getStoredUTM,
  getStoredClickIds,
} from "@/lib/tracking";

let _scriptLoading = null;

// Lazy-load the LS overlay SDK once. Returns a promise that resolves when
// `window.createLemonSqueezy` is callable.
export function loadLemonSqueezyScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.createLemonSqueezy) return Promise.resolve();
  if (_scriptLoading) return _scriptLoading;
  _scriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://app.lemonsqueezy.com/js/lemon.js";
    s.defer = true;
    s.onload = () => {
      try {
        // SDK exposes createLemonSqueezy; calling it attaches LemonSqueezy.Url.Open
        if (typeof window.createLemonSqueezy === "function") window.createLemonSqueezy();
        resolve();
      } catch (e) {
        reject(e);
      }
    };
    s.onerror = () => reject(new Error("LS script failed to load"));
    document.head.appendChild(s);
  });
  return _scriptLoading;
}

// Build a buy-URL with our checkout-time attribution baked in as custom data.
// LS surfaces these back to the webhook so we can attribute the purchase to
// a Meta/Google ad click + a specific visitor session.
export function buildCheckoutUrl(product) {
  const storeSlug = product.ls_store_slug || "dharmaweave";
  const variantId = product.ls_variant_id;
  if (!variantId) return null;

  const utm = getStoredUTM() || {};
  const clickIds = getStoredClickIds() || {};

  const customParams = {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    product_slug: product.id,
    product_kind: product.kind,
    page_url: typeof window !== "undefined" ? window.location.href : "",
    fbclid: clickIds.fbclid || "",
    gclid: clickIds.gclid || "",
    utm_source: utm.utm_source || "",
    utm_medium: utm.utm_medium || "",
    utm_campaign: utm.utm_campaign || "",
    utm_content: utm.utm_content || "",
    utm_term: utm.utm_term || "",
  };

  const qs = Object.entries(customParams)
    .filter(([, v]) => v)
    .map(([k, v]) => `checkout[custom][${encodeURIComponent(k)}]=${encodeURIComponent(v)}`)
    .join("&");

  return `https://${storeSlug}.lemonsqueezy.com/buy/${variantId}?${qs}`;
}

// Open the LS overlay checkout for a product. Returns a promise that resolves
// when the overlay is open (LS handles the rest).
export async function openCheckout(product) {
  const url = buildCheckoutUrl(product);
  if (!url) {
    console.warn("[LemonSqueezy] product has no ls_variant_id — cannot open checkout", product);
    return false;
  }
  try {
    await loadLemonSqueezyScript();
    if (window.LemonSqueezy && window.LemonSqueezy.Url) {
      window.LemonSqueezy.Url.Open(url);
      return true;
    }
    // Fallback: full-page navigation if SDK didn't attach
    window.location.href = url;
    return true;
  } catch (e) {
    console.error("[LemonSqueezy] checkout open failed", e);
    window.location.href = url;
    return false;
  }
}
